import { Decimal, pct } from './money';

/**
 * POS custom fee types — admin-managed registry for the cashier's "custom fee"
 * charge, mirroring the `pos-payment-methods` pattern (config in
 * `tenant_config.features.pos_custom_fees.types[]`, no dedicated table, typed
 * admin editor — no raw JSON).
 *
 * The cashier used to TYPE the label. The load-bearing part of this change is
 * not the dropdown, it's that the cashier no longer sends a label at all: the
 * server resolves it from the registry code, so "Delvery" becomes
 * unrepresentable and the daily report can finally group by fee type.
 *
 * The admin sets the LABEL only. The amount stays per-sale: a delivery fee
 * varies by distance, and pinning it would make the feature unusable for the
 * case that motivated it.
 */

/**
 * How a fee's amount is arrived at.
 *
 *  - `manual`  — the cashier types it. The original behaviour, and still right
 *                for a delivery fee that varies by distance.
 *  - `percent` — `value`% of the RUNNING total (see `computeOrderCustomFees`).
 *  - `flat`    — an amount the admin pins, so no till can key it differently.
 *
 * ABSENT means `manual`, which is what keeps every existing tenant's registry
 * working untouched — the same opt-in discipline `payout_deductions.order` uses.
 */
export type PosCustomFeeCalc =
  | { kind: 'manual' }
  | { kind: 'percent'; value: number }
  | { kind: 'flat'; value: number };

export type PosCustomFeeType = {
  /** Stable slug stored on `orders.custom_fees[].code`. */
  code: string;
  /** What the cashier picks and what prints on the tape. */
  label: string;
  enabled: boolean;
  /** Absent ⇒ `{ kind: 'manual' }`. */
  calc?: PosCustomFeeCalc;
};

/** The calc for a type, defaulting an absent one to manual. */
export function feeCalcOf(t: Pick<PosCustomFeeType, 'calc'>): PosCustomFeeCalc {
  return t.calc ?? { kind: 'manual' };
}

export type PosCustomFeesConfig = {
  types: PosCustomFeeType[];
};

/**
 * EMPTY by default, unlike `POS_PAYMENT_METHODS_DEFAULT`.
 *
 * A POS is unusable without a tender, so that registry had to ship with one.
 * Custom fees are the opposite: seeding a "Delivery" button for a tenant that
 * doesn't charge one puts a wrong control in front of every cashier. An empty
 * registry hides the fee block at the till entirely.
 */
export const POS_CUSTOM_FEES_DEFAULT: PosCustomFeesConfig = { types: [] };

/**
 * Resolve a fee type by code from a fee LIST — normally the one the selected POS
 * payment method carries (`posMethodFeeTypes`), which falls back to the legacy
 * global `pos_custom_fees.types`. Returns `null` when the code is absent or the
 * type is DISABLED — a disabled type must not silently still be chargeable,
 * which is the whole point of being able to retire one.
 */
export function resolvePosCustomFeeType(
  types: readonly PosCustomFeeType[] | null | undefined,
  code: string,
): PosCustomFeeType | null {
  if (!Array.isArray(types)) return null;
  const found = types.find((t) => t.code === code);
  if (!found || !found.enabled) return null;
  return found;
}

/** Build a blank, enabled type for the admin editor's "add row". */
export function makePosCustomFeeType(code = '', label = ''): PosCustomFeeType {
  return { code, label, enabled: true, calc: { kind: 'manual' } };
}

/** One fee as stored on `orders.custom_fees` and rendered on a receipt. */
export type OrderCustomFee = {
  /** Registry code, or null for a fee placed before the registry existed. */
  code: string | null;
  /** Snapshotted at sale time — renaming the type never rewrites this. */
  label: string;
  amount: string;
};

/** The subset of an order these helpers need, so callers can pass any DTO. */
export type CustomFeeBearingOrder = {
  customFees?: OrderCustomFee[] | null;
  customFeeLabel?: string | null;
  customFeeAmount?: string | number | null;
};

/**
 * The fee lines to render for an order — THE single source for every renderer:
 * the cashier tape, the reprint, the admin order drawer.
 *
 * Orders placed before `orders.custom_fees` existed carry `[]` plus the legacy
 * label/amount pair, so this synthesises one line from those. Every renderer
 * must call this and only this, or the same historical order shows a fee in one
 * place and nothing in another.
 *
 * `'Other fee'` is the fallback label because that is what the admin order
 * drawer already displayed for a fee whose label was never recorded.
 */
export function orderCustomFeeLines(
  order: CustomFeeBearingOrder,
): Array<{ label: string; amount: string }> {
  const fees = order.customFees;
  if (Array.isArray(fees) && fees.length > 0) {
    return fees.map((f) => ({ label: f.label, amount: f.amount }));
  }
  const legacy = Number(order.customFeeAmount ?? 0);
  if (!Number.isFinite(legacy) || legacy <= 0) return [];
  return [
    { label: order.customFeeLabel?.trim() || 'Other fee', amount: String(order.customFeeAmount) },
  ];
}

/** One fee as the caller hands it to `computeOrderCustomFees`. */
export type SelectedCustomFee = {
  code: string | null;
  label: string;
  calc: PosCustomFeeCalc;
  /** Only read for `kind: 'manual'`; ignored otherwise. */
  amount?: string;
};

export type ComputedCustomFees = {
  /** Ready to persist to `orders.custom_fees`, in applied order. */
  lines: OrderCustomFee[];
  /** Σ lines — what gets added to `orders.total`. */
  total: Decimal;
};

/**
 * Price a sale's custom fees as an ADDITIVE CASCADE, the mirror of what
 * `computePayoutDeductions` does for a cash-out. Each fee is computed on the
 * RUNNING total left by the fees before it, so a percentage stacks on top of
 * what is already there:
 *
 *   goods 500 → tax 10% (+50) → 550 → handling flat 50 (+50) → 600
 *
 * That stacking is the whole point of the ordering: with a percentage fee,
 * "tax then handling" and "handling then tax" are genuinely different totals.
 * The ORDER is the admin's registry order — there is no second `order` array to
 * drift out of sync — and the caller must pass `selected` already sorted that
 * way. The POS service sorts SERVER-SIDE so a tampered client cannot reorder
 * its way to a cheaper total.
 *
 * Sign is the one real difference from the payout cascade: a deduction whittles
 * a balance down and has to be clamped at zero, whereas a fee only ever adds, so
 * the running total cannot go negative and needs no clamp. A negative fee is
 * rejected by the caller (`custom_fee_negative`); markdowns go through the
 * manager discount, which is capped and PIN-gated.
 *
 * Pure + Decimal-only: this file is in the `no-float-money` lint scope.
 */
export function computeOrderCustomFees(
  base: Decimal,
  selected: SelectedCustomFee[],
): ComputedCustomFees {
  let running = base;
  let total = new Decimal(0);
  const lines: OrderCustomFee[] = [];

  for (const f of selected) {
    let amount: Decimal;
    switch (f.calc.kind) {
      case 'percent':
        amount = pct(running, f.calc.value);
        break;
      case 'flat':
        amount = new Decimal(f.calc.value);
        break;
      default:
        amount = new Decimal(f.amount ?? 0);
    }
    // A zero fee is not a line. It would print an empty row on the tape and
    // add a meaningless entry to the persisted breakdown — the same reason the
    // payout breakdown skips a step whose amount is zero.
    if (amount.lte(0)) continue;
    running = running.plus(amount);
    total = total.plus(amount);
    lines.push({ code: f.code, label: f.label, amount: amount.toFixed(4) });
  }

  return { lines, total };
}

/** Max length of the composed `orders.custom_fee_label` one-liner. */
const COMPOSED_LABEL_MAX = 120;

/**
 * The single-line label written to the legacy `orders.custom_fee_label` column.
 *
 * ONE fee composes to exactly what it composed to before this feature existed,
 * so a single-fee order is byte-identical to the old behaviour. Several join
 * with ` + `, truncated — the column feeds summaries and exports that assume a
 * short string, not a receipt.
 */
export function composeCustomFeeLabel(fees: Array<{ label: string }>): string | null {
  if (fees.length === 0) return null;
  const joined = fees.map((f) => f.label).join(' + ');
  return joined.length <= COMPOSED_LABEL_MAX
    ? joined
    : `${joined.slice(0, COMPOSED_LABEL_MAX - 1)}…`;
}
